import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import Seo from '../components/Seo';
import { getPersonelPage, STRAPI_URL } from '../services/api';
import DynamicIcon from '../components/DynamicIcon';
import DEFAULT_PERSONEL_DATA from '../default_datas/pages/defaultPersonel';

const Personel = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tx = (tr, en) => lang === 'en' ? (en || tr) : tr;

  useEffect(() => {
    getPersonelPage()
      .then(result => { setData(result); setLoading(false); })
      .catch(err => { console.error('Strapi fetch hatası:', err); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#2a9d8f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sections = data?.pageContent || [];
  const hero = { ...DEFAULT_PERSONEL_DATA.hero, ...(sections.find(s => s.__component === 'sections.hero') ?? {}) };
  const statsSection = { ...DEFAULT_PERSONEL_DATA.stats, ...(sections.find(s => s.__component === 'sections.stats-section') ?? {}) };
  const features = { ...DEFAULT_PERSONEL_DATA.features, ...(sections.find(s => s.__component === 'sections.features-section') ?? {}) };
  const preHire = { ...DEFAULT_PERSONEL_DATA.preHire, ...(sections.find(s => s.__component === 'sections.pre-hire-section') ?? {}) };
  const offboarding = { ...DEFAULT_PERSONEL_DATA.offboarding, ...(sections.find(s => s.__component === 'sections.offboarding-section') ?? {}) };
  const awareness = { ...DEFAULT_PERSONEL_DATA.awareness, ...(sections.find(s => s.__component === 'sections.awareness-section') ?? {}) };
  const cta = { ...DEFAULT_PERSONEL_DATA.cta, ...(sections.find(s => s.__component === 'sections.cta') ?? {}) };

  const skor = awareness?.skor ?? 85;
  const strokeOffset = 314 - (314 * skor) / 100;

  return (
    <>
      <Seo
        title="Personel Güvenliği ve Yönetimi | RUZY"
        description="Rol yönetimi, farkındalık eğitimleri, erişim kontrolü, işe alım öncesi güvenlik taramaları ve işten ayrılma süreçlerini ISO 27001 uyumlu yönetin."
        path="/personel"
      />

      <div className="font-poppins text-[#0D3A2F] overflow-x-hidden">

        {/* ── 1. HERO ── */}
        {hero && (
          <ScrollReveal>
            {/* DEĞİŞİKLİK: flex-col → flex-col lg:flex-row, ortalama kaldırıldı, sol hizalı yapıldı */}
            <section className="flex flex-col lg:flex-row items-center justify-between px-6 lg:px-28 py-12 lg:py-24 bg-[#f8fbfa] gap-10 lg:gap-16">

              {/* Sol: Metin + Butonlar */}
              <div className="flex-1 max-w-full lg:max-w-[520px] text-center lg:text-left">
                {hero.badge && (
                  <div className="inline-flex items-center gap-1.5 bg-[#e0f2ef] text-[#2a9d8f] text-[11px] lg:text-xs font-bold px-5 py-2 rounded-full mb-6 tracking-wide border border-[#b2dfd8]">
                    <span className="material-symbols-outlined text-sm">verified</span>
                    {tx(hero.badge, hero.badge_en).toUpperCase()}
                  </div>
                )}
                <h1 className="text-3xl lg:text-[54px] font-black leading-[1.1] mb-6 text-[#0D3A2F]">
                  {tx(hero.title, hero.title_en)}
                </h1>
                <p className="text-[15px] lg:text-lg leading-relaxed text-[#4a6b63] mb-10">
                  {tx(hero.description, hero.description_en)}
                </p>

                {/* Butonlar */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
                  <button
                    onClick={() => navigate('/iletisim')}
                    className="w-full sm:w-[240px] h-14 inline-flex items-center justify-center gap-2 bg-[#2a9d8f] text-white rounded-xl text-base font-bold shadow-lg shadow-[#2a9d8f]/20 hover:bg-[#238a7e] transition-all"
                  >
                    {tx(hero.primaryButtonText, hero.primaryButtonText_en) || (lang === 'en' ? 'Try for Free' : 'Ücretsiz Deneyin')}
                    <span className="material-symbols-outlined text-lg">arrow_forward</span>
                  </button>
                  <button
                    onClick={() => navigate('/iletisim')}
                    className="w-full sm:w-[240px] h-14 bg-white text-[#0D3A2F] border-2 border-[#0D3A2F] rounded-xl text-base font-bold hover:bg-[#0D3A2F] hover:text-white transition-all"
                  >
                    {tx(hero.secondaryButtonText, hero.secondaryButtonText_en) || (lang === 'en' ? 'Documentation' : 'Dokümantasyon')}
                  </button>
                </div>
              </div>

              {/* Sağ: Tablo Önizleme */}
              <div className="flex-1 flex justify-center w-full">
                <div className="relative w-full max-w-[500px]">
                  <div className="bg-[#e8f5f1] rounded-[32px] p-4 lg:p-6 shadow-2xl shadow-[#2a9d8f]/10">
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#f0f0f0]">
                      {/* Browser Dots */}
                      <div className="px-5 py-4 flex gap-2 border-b border-[#f0f0f0]">
                        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                      </div>

                      {/* Table Header */}
                      <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-[#f8fbfa]">
                        {[lang === 'en' ? 'NAME' : 'AD', lang === 'en' ? 'DEPT.' : 'DEP.', lang === 'en' ? 'STATUS' : 'DURUM'].map(h => (
                          <span key={h} className="text-[11px] font-black text-[#4a6b63] tracking-widest">{h}</span>
                        ))}
                      </div>

                      {/* Mock Rows */}
                      {(hero.mockRows || [{ ad: 'Ahmet Y.', departman: 'IT' }, { ad: 'Zeynep K.', departman: 'HR' }]).slice(0, 2).map((row, i) => (
                        <div key={i} className={`grid grid-cols-3 gap-4 px-6 py-5 border-b border-[#f0f0f0] last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafffe]'}`}>
                          <span className="text-xs font-bold text-[#0D3A2F] text-left truncate">{row.ad}</span>
                          <span className="text-xs text-[#4a6b63] text-left truncate">{row.departman}</span>
                          <span className="text-[10px] font-black text-[#2a9d8f] text-left">{lang === 'en' ? 'ACTIVE' : 'AKTİF'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

            </section>
          </ScrollReveal>
        )}

        {/* ── 2. STATS ── */}
        {statsSection?.stats?.length > 0 && (
          <ScrollReveal>
            <section className="flex flex-col lg:flex-row gap-4 lg:gap-6 px-6 lg:px-28 py-10 bg-white">
              {statsSection.stats.map((s, i) => (
                <div key={i} className="flex-1 border border-[#e0eeeb] rounded-2xl px-8 py-6 flex flex-row lg:flex-col justify-between items-center lg:items-start gap-2 hover:border-[#2a9d8f] transition-colors group">
                  <span className="text-[11px] font-bold text-[#6b9e96] uppercase tracking-widest group-hover:text-[#2a9d8f] transition-colors">
                    {tx(s.title, s.title_en)}
                  </span>
                  {/* DEĞİŞİKLİK: trend etiketi eklendi */}
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl lg:text-4xl font-black text-[#0D3A2F]">
                      {tx(s.value, s.value_en) || s.value}
                    </span>
                    {(s.subText || s.subText_en) && (
                      <span className="text-xs font-bold text-[#2a9d8f]">
                        {tx(s.subText, s.subText_en)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </section>
          </ScrollReveal>
        )}

        {/* ── 3. TEMEL ÖZELLİKLER ── */}
        {features && (
          <ScrollReveal>
            <section className="px-6 lg:px-28 py-16 lg:py-24 bg-[#f5faf9]">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl lg:text-4xl font-black text-[#0D3A2F] mb-4">
                    {tx(features.Title, features.Title_en)}
                  </h2>
                  {features.Description && (
                    <p className="text-sm lg:text-base text-[#4a6b63] max-w-[560px] mx-auto">
                      {tx(features.Description, features.Description_en)}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(features.cards || []).map((f, i) => (
                    <div key={i} className="bg-white rounded-2xl p-8 border border-[#e0eeeb] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
                      <div className="text-[#2a9d8f] mb-6">
                        <DynamicIcon name={f.icon} size={32} />
                      </div>
                      <h3 className="text-lg font-bold text-[#0D3A2F] mb-3">
                        {tx(f.title, f.title_en)}
                      </h3>
                      <p className="text-[14px] leading-relaxed text-[#4a6b63] flex-1">
                        {tx(f.description, f.description_en)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {/* ── 4. İŞE ALIM ÖNCESİ ── */}
        {preHire && (
          <ScrollReveal>
            <section className="px-6 lg:px-28 py-16 lg:py-24 bg-white">
              <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <div className="order-2 lg:order-1">
                  <div className="grid grid-cols-2 gap-4">
                    {(preHire.ikonluKartlar || []).map((c, i) => (
                      <div key={i} className="bg-[#f8fbfa] border border-[#e0eeeb] rounded-2xl p-6 flex flex-col items-center gap-3 text-center hover:bg-[#e0f2ef] transition-colors">
                        <span className="material-symbols-outlined text-4xl text-[#2a9d8f]">{c.icon}</span>
                        <span className="text-xs font-bold text-[#0D3A2F] uppercase tracking-wide">{tx(c.label, c.label_en)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="order-1 lg:order-2 text-center lg:text-left">
                  <h2 className="text-3xl lg:text-4xl font-black text-[#0D3A2F] mb-6">
                    {tx(preHire.baslik, preHire.baslik_en)}
                  </h2>
                  <p className="text-base leading-relaxed text-[#4a6b63] mb-8">
                    {tx(preHire.aciklama, preHire.aciklama_en)}
                  </p>
                  <ul className="grid gap-4 text-left">
                    {(preHire.maddeler || []).slice(0, 4).map((m, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm font-medium text-[#0D3A2F]">
                        <span className="material-symbols-outlined text-[#2a9d8f] flex-shrink-0">check_circle</span>
                        {tx(m.Title, m.title_en)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {/* ── 5. İŞTEN AYRILMA ── */}
        {offboarding && (
          <ScrollReveal>
            <section className="px-6 lg:px-28 py-16 lg:py-24 bg-[#f5faf9]">
              <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <div className="text-center lg:text-left">
                  <h2 className="text-3xl lg:text-4xl font-black text-[#0D3A2F] mb-6">
                    {tx(offboarding.baslik, offboarding.baslik_en)}
                  </h2>
                  {offboarding.aciklama && (
                    <p className="text-base leading-relaxed text-[#4a6b63] mb-8">
                      {tx(offboarding.aciklama, offboarding.aciklama_en)}
                    </p>
                  )}
                  <div className="flex flex-col gap-4">
                    {(offboarding.kartlar || []).slice(0, 2).map((k, i) => (
                      <div key={i} className="flex items-center gap-4 bg-white border border-[#e0eeeb] rounded-2xl p-5 shadow-sm">
                        <div className="text-[#2a9d8f] bg-[#f0f9f7] p-2.5 rounded-lg flex-shrink-0">
                          <DynamicIcon name={k.icon} size={26} />
                        </div>
                        <div>
                          <h4 className="text-base font-bold text-[#0D3A2F]">
                            {tx(k.title, k.title_en)}
                          </h4>
                          {k.description && (
                            <p className="text-xs text-[#4a6b63] mt-1 leading-relaxed">
                              {tx(k.description, k.description_en)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#2a9d8f] rounded-3xl p-8 text-white shadow-2xl shadow-[#2a9d8f]/30">
                  <div className="flex justify-between items-center mb-8">
                    <span className="text-lg font-black uppercase tracking-widest">{tx(offboarding.checklistBaslik, offboarding.checklistBaslik_en) || 'Checklist'}</span>
                    <span className="material-symbols-outlined text-3xl">checklist</span>
                  </div>
                  <div className="space-y-4">
                    {(offboarding.checklistItems || []).slice(0, 4).map((item, i) => (
                      <div key={i} className="flex items-center gap-4 py-4 border-b border-white/10 last:border-0">
                        <span className="material-symbols-outlined text-2xl text-white/50">
                          {item.tamamlandi ? 'check_box' : 'check_box_outline_blank'}
                        </span>
                        <span className="text-sm font-medium">{tx(item.text, item.text_en)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {/* ── 6. GÜVENLİK FARKINDALIK ── */}
        {awareness && (
          <section className="px-6 lg:px-28 py-16 lg:py-24 bg-[#0D3A2F]">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                <h2 className="text-3xl lg:text-4xl font-black text-white mb-6">
                  {tx(awareness.baslik, awareness.baslik_en)}
                </h2>
                {awareness.aciklama && (
                  <p className="text-sm lg:text-base text-white/70 mb-8 leading-relaxed">
                    {tx(awareness.aciklama, awareness.aciklama_en)}
                  </p>
                )}
                <div className="grid gap-5 mt-8">
                  {(awareness.kartlar || []).slice(0, 2).map((k, i) => (
                    <div key={i} className="flex items-start gap-4 text-left bg-white/5 p-4 rounded-xl border border-white/10">
                      <div className="text-[#2a9d8f] bg-[#2a9d8f]/20 rounded-lg p-2.5 flex-shrink-0">
                        <DynamicIcon name={k.icon} size={22} />
                      </div>
                      <div>
                        <h4 className="text-sm lg:text-base font-bold text-white mb-1">
                          {tx(k.title, k.title_en)}
                        </h4>
                        {k.description && (
                          <p className="text-xs text-white/60 leading-relaxed">
                            {tx(k.description, k.description_en)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-[32px] p-10 text-white text-center">
                <span className="text-xs font-black text-white/60 block mb-8 uppercase tracking-[4px]">
                  {lang === 'en' ? 'AWARENESS SCORE' : 'FARKINDALIK SKORU'}
                </span>
                <div className="relative w-40 h-40 lg:w-52 lg:h-52 mx-auto mb-8">
                  <svg viewBox="0 0 120 120" className="w-full h-full drop-shadow-[0_0_15px_rgba(42,157,143,0.3)]">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                    <circle
                      cx="60" cy="60" r="50"
                      fill="none" stroke="#2a9d8f" strokeWidth="8"
                      strokeDasharray="314"
                      strokeDashoffset={strokeOffset}
                      strokeLinecap="round"
                      transform="rotate(-90 60 60)"
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl lg:text-6xl font-black text-white">{skor}</span>
                  </div>
                </div>
                {/* DEĞİŞİKLİK: Tamamlanma oranı progress bar eklendi */}
                <div className="w-full">
                  <div className="flex justify-between text-[10px] font-bold text-white/50 mb-2 uppercase tracking-widest">
                    <span>{lang === 'en' ? 'COMPLETION RATE' : 'TAMAMLANMA ORANI'}</span>
                    <span>%{skor}</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#2a9d8f] rounded-full transition-all duration-1000"
                      style={{ width: `${skor}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── 7. CTA ── */}
        {cta && (
          <section className="bg-[#0D3A2F] px-6 py-12 lg:py-24">
            <div className="max-w-5xl mx-auto bg-gradient-to-br from-[#2a9d8f] to-[#15614F] rounded-[40px] p-10 lg:p-20 text-center shadow-2xl relative overflow-hidden">
              <div className="relative z-10">
                <h2 className="text-3xl lg:text-5xl font-black text-white mb-6">
                  {tx(cta.Title, cta.Title_en)}
                </h2>
                <p className="text-base lg:text-lg text-white/90 mb-12 max-w-[600px] mx-auto leading-relaxed">
                  {tx(cta.Description, cta.Description_en)}
                </p>
                {/* DEĞİŞİKLİK: İki buton yan yana */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/iletisim')}
                    className="h-16 px-12 bg-white text-[#0D3A2F] border-none rounded-2xl text-lg font-black shadow-xl hover:scale-105 transition-all"
                  >
                    {tx(cta.primaryButtonText, cta.primaryButtonText_en)}
                  </button>
                  {cta.secondaryButtonText && (
                    <button
                      onClick={() => navigate('/iletisim')}
                      className="h-16 px-12 bg-transparent text-white border-2 border-white rounded-2xl text-lg font-black hover:bg-white/10 transition-all"
                    >
                      {tx(cta.secondaryButtonText, cta.secondaryButtonText_en)}
                    </button>
                  )}
                </div>
              </div>
              <span className="material-symbols-outlined absolute -bottom-10 -right-10 text-[200px] text-white/5 pointer-events-none">security</span>
            </div>
          </section>
        )}

      </div>
    </>
  );
};

export default Personel;